import type { ReactNode } from "react";
import React from "react";
import { AppConfigContext } from "../app/appConfig";
import { useAlert } from "../app/notificationToast";
import { FollowupModel } from "../../api/followup/FollowupModel";
import { FollowupAdapter } from "../../api/followup/FollowupAdapter";
import type { FollowUpSheet, Dive } from "../../api/followup/Followup.typed";

interface FollowupContextType {
  getFollowUpSheet: (templateId: number, customerId: number) => Promise<FollowUpSheet | undefined>;
  createFollowUpSheet: (sheet: FollowUpSheet) => Promise<void>;
  updateFollowUpSheet: (sheet: FollowUpSheet) => Promise<FollowUpSheet | undefined>;
  getDives: (templateId: number, customerId: number) => Promise<Dive[] | undefined>;
  updateDives: (
    templateId: number,
    customerId: number,
    dives: Dive[],
  ) => Promise<Dive[] | undefined>;
}

const FollowupContext = React.createContext<FollowupContextType>(null!);

export function FollowupProvider(props: { children: ReactNode }) {
  const appConfig = React.useContext(AppConfigContext);
  const { showAlert } = useAlert();

  const adapter = React.useMemo(() => {
    if (!appConfig) return null;
    const model = new FollowupModel(appConfig);
    return new FollowupAdapter(model);
  }, [appConfig]);

  const wrap = React.useCallback(
    async <T,>(fn: () => Promise<T>): Promise<T | undefined> => {
      try {
        return await fn();
      } catch (err) {
        showAlert(err instanceof Error ? err.message : "Erreur", "danger");
        return undefined;
      }
    },
    [showAlert],
  );

  const getFollowUpSheet = React.useCallback(
    async (templateId: number, customerId: number): Promise<FollowUpSheet | undefined> =>
      adapter ? wrap(() => adapter.getFollowUpSheet(templateId, customerId)) : undefined,
    [adapter, wrap],
  );

  const createFollowUpSheet = React.useCallback(
    async (sheet: FollowUpSheet): Promise<void> => {
      if (!adapter) return;
      try {
        await adapter.createFollowUpSheet(sheet);
        showAlert("Fiche de suivi créée", "success");
      } catch (err) {
        showAlert(err instanceof Error ? err.message : "Erreur", "danger");
      }
    },
    [adapter, showAlert],
  );

  const updateFollowUpSheet = React.useCallback(
    async (sheet: FollowUpSheet): Promise<FollowUpSheet | undefined> => {
      if (!adapter) return undefined;
      try {
        const updated = await adapter.updateFollowUpSheet(sheet);
        showAlert("Fiche de suivi mise à jour", "success");
        return updated;
      } catch (err) {
        showAlert(err instanceof Error ? err.message : "Erreur", "danger");
        return undefined;
      }
    },
    [adapter, showAlert],
  );

  const getDives = React.useCallback(
    async (templateId: number, customerId: number): Promise<Dive[] | undefined> =>
      adapter ? wrap(() => adapter.getDives(templateId, customerId)) : undefined,
    [adapter, wrap],
  );

  const updateDives = React.useCallback(
    async (templateId: number, customerId: number, dives: Dive[]): Promise<Dive[] | undefined> => {
      if (!adapter) return undefined;
      try {
        const updated = await adapter.updateDives(templateId, customerId, dives);
        showAlert("Plongées mises à jour", "success");
        return updated;
      } catch (err) {
        showAlert(err instanceof Error ? err.message : "Erreur", "danger");
        return undefined;
      }
    },
    [adapter, showAlert],
  );

  const value = React.useMemo<FollowupContextType>(
    () => ({
      getFollowUpSheet,
      createFollowUpSheet,
      updateFollowUpSheet,
      getDives,
      updateDives,
    }),
    [getFollowUpSheet, createFollowUpSheet, updateFollowUpSheet, getDives, updateDives],
  );

  return <FollowupContext.Provider value={value}>{props.children}</FollowupContext.Provider>;
}

export function useFollowup(): FollowupContextType {
  const ctx = React.useContext(FollowupContext);
  if (!ctx) throw new Error("useFollowup must be used within FollowupProvider");
  return ctx;
}
